import {
  Component,
  ChangeDetectorRef,
  computed,
  signal,
  ViewChild,
  OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Observable, EMPTY } from 'rxjs';
import { tap, map, catchError } from 'rxjs/operators';
import { TableComponent } from '@view/shared/molecules/app-table/app-table.component';
import { PaginatorComponent } from '@view/shared/molecules/paginator/paginator.component';
import { ButtonComponent } from '@view/shared/molecules/button/button.component';
import {
  ButtonTypeEnum,
  ButtonSizeEnum
} from '@view/shared/molecules/button/buttonTypesEnum';
import { TableActionType } from '@view/shared/molecules/app-table/tableActionTypes';
import { ReceivedOrder, IngestedOrdersQueryParams } from '@model/ingested-order/ingested-order.model';
import { ReceivedOrdersPresenter } from '../../presenters/received-orders.presenter';
import { IOrderMetricsPort } from '../../../../domain/service/preclinical/order-metrics.port';
import { IFilterOptionsQueryPort } from '../../../../domain/service/preclinical/filter-options-query.port';
import { MetricsDashboardComponent } from '@view/shared/molecules/metrics-dashboard/metrics-dashboard.component';
import { ToastComponent } from '@view/shared/molecules/toast/toast.component';
import { ToastService } from '@view/shared/molecules/toast/service/toast.service';
import { ToastTypeEnum } from '@view/shared/molecules/toast/toast-type-enum';
import { OrderStatus } from '../../../../domain/types/order-status.type';
import {
  ColumnOption,
  FrozenColumnsSidebarComponent
} from '@view/preclinical/molecules/frozen-columns-sidebar/frozen-columns-sidebar.component';
import { FilterSidebarComponent } from '../../molecules/filter-sidebar/filter-sidebar.component';
import { ActiveFiltersComponent } from '@view/shared/molecules/active-filters/active-filters.component';
import { ExportModalState } from '../../molecules/export-confirmation-modal/export-confirmation-modal.component';
import { BaseOrdersListComponent } from '../base-orders-list.component';
import { FrozenColumnsService } from '@helpers/table/frozen-columns.service';
import { SearchControlsComponent } from '@view/shared/molecules/search-controls/search-controls.component';
import {
  FinalSaveModalComponent,
  FinalSaveModalState
} from '../../molecules/final-save-modal/final-save-modal.component';
import type { FinalSaveResponse } from '../../../../infrastructure/services/received-orders/services/received-orders.service';
import { EditSampleTabsComponent } from '../add-sample/edit-sample/edit-sample-tabs/edit-sample-tabs.component';
import { FloatingSidebarComponent } from '@view/shared/molecules/floating-sidebar/floating-sidebar.component';
import { ManualEntryPayload } from '@model/patient/sample-entry.model';
import { GenericModalComponent } from '@view/shared/organisms/generic-modal/generic-modal.component';
import { RECEIVED_ORDER_SEARCH_OPTIONS } from '../../config/search-options.config';

@Component({
  selector: 'app-received-orders-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableComponent,
    PaginatorComponent,
    ButtonComponent,
    MetricsDashboardComponent,
    FrozenColumnsSidebarComponent,
    ToastComponent,
    SearchControlsComponent,
    FilterSidebarComponent,
    ActiveFiltersComponent,
    FinalSaveModalComponent,
    EditSampleTabsComponent,
    FloatingSidebarComponent,
    GenericModalComponent
  ],
  templateUrl: './received-orders-list.component.html',
  styleUrls: ['./received-orders-list.component.scss'],
  providers: [
    FrozenColumnsService,
    {
      provide: 'FROZEN_COLUMNS_STORAGE_KEY',
      useValue: 'sig-frozen-columns-received-orders'
    }
  ]
})
export class ReceivedOrdersListComponent
  extends BaseOrdersListComponent
  implements OnDestroy
{
  orders: ReceivedOrder[] = [];
  override pageSize = 10;
  override totalRecords = 0;

  selectedOrders: ReceivedOrder[] = [];

  private readonly _selectedOrderIds = signal(new Set<string | number>());
  readonly selectedOrderIds = this._selectedOrderIds.asReadonly();

  private readonly _isProcessing = signal(false);
  readonly isProcessing = this._isProcessing.asReadonly();

  private readonly _finalSaveModalOpen = signal(false);
  readonly finalSaveModalOpen = this._finalSaveModalOpen.asReadonly();

  private readonly _finalSaveModalState = signal(
    FinalSaveModalState.CONFIRMATION
  );
  readonly finalSaveModalState = this._finalSaveModalState.asReadonly();

  private readonly _finalSaveResponse = signal<FinalSaveResponse | null>(null);

  showEditSampleModal = signal(false);
  editCaseNumber = signal<string | null>(null);
  editSampleData = signal<ManualEntryPayload | null>(null);

  showDeleteConfirmModal = signal(false);
  deleteSpecimenCode = signal<string | null>(null);
  isDeleting = signal(false);

  readonly finalSaveResponse = this._finalSaveResponse.asReadonly();

  readonly selectedCount = computed(() => this._selectedOrderIds().size);

  readonly hasSelectedOrders = computed(
    () => this._selectedOrderIds().size > 0
  );

  @ViewChild(PaginatorComponent) paginator!: PaginatorComponent;

  override readonly ButtonTypeEnum = ButtonTypeEnum;
  override readonly ButtonSizeEnum = ButtonSizeEnum;

  override readonly ExportModalState = ExportModalState;

  private readonly ICON_DOCUMENTO = 'icon-documento';
  private readonly ICON_CARNET = 'icon-identificacion';
  private readonly ICON_CALENDARIO = 'icon-calendario-fecha';
  private readonly ICON_USUARIO = 'icon-usuario';
  private readonly ICON_UBICACION = 'icon-ubicacion';
  private readonly ICON_EXCLAMACION = 'icon-hexagono-exclamacion-rojo';
  private readonly ICON_PRIORIDAD = 'icon-prioridad';
  private readonly ICON_CLIPBOARD = 'icon-clipboard';
  private readonly ICON_FICHA_NOMBRE = 'icon-ficha-nombre';
  private readonly ICON_HEXAGONO = 'icon-hexagono-exclamacion';
  private readonly ICON_OBSERVACION = 'icon-observacion';
  private readonly ICON_RECIBO = 'icon-recibo';

  readonly ALL_COLUMNS = [
    {
      key: 'isReprocess',
      label: 'Reproceso',
      searchable: false,
      icon: this.ICON_HEXAGONO,
      type: 'boolean-icon' as const,
      booleanIcon: this.ICON_EXCLAMACION
    },
    {
      key: 'reprocessCause',
      label: 'Causa del reproceso',
      searchable: false,
      icon: this.ICON_HEXAGONO
    },
    {
      key: 'reprocessObservation',
      label: 'Observacion',
      searchable: false,
      icon: this.ICON_OBSERVACION
    },
    {
      key: 'priority',
      label: 'Prioridad',
      searchable: false,
      icon: this.ICON_PRIORIDAD,
      type: 'priority' as const
    },
    {
      key: 'lgNumber',
      label: 'LG',
      searchable: false,
      icon: this.ICON_RECIBO
    },
    {
      key: 'caseNumber',
      label: 'Codigo de muestra',
      searchable: true,
      icon: this.ICON_RECIBO
    },
    {
      key: 'identificationNumber',
      label: 'ID paciente',
      searchable: true,
      icon: this.ICON_CARNET
    },
    {
      key: 'patientFirstName',
      label: 'Nombre',
      searchable: true,
      icon: this.ICON_FICHA_NOMBRE
    },
    {
      key: 'patientLastName',
      label: 'Apellido',
      searchable: true,
      icon: this.ICON_FICHA_NOMBRE
    },
    {
      key: 'admissionDate',
      label: 'Fecha de ingreso',
      searchable: false,
      icon: this.ICON_CALENDARIO,
      type: 'date' as const,
      tooltip: {
        pipe: 'fullDateTime' as const,
        alwaysShow: true
      }
    },
    {
      key: 'examName',
      label: 'Tipo de examen',
      searchable: false,
      icon: this.ICON_CLIPBOARD
    },
    {
      key: 'examCode',
      label: 'Codigo examen',
      searchable: false,
      icon: this.ICON_CLIPBOARD
    }
  ];

  private readonly ALL_COLUMN_KEYS = this.ALL_COLUMNS.filter(
    (col) => col.key !== 'actions'
  ).map((col) => col.key);

  readonly availableColumns: ColumnOption[] = this.ALL_COLUMNS.filter(
    (col) => col.key !== 'actions'
  ).map((col) => ({
    key: col.key,
    label: col.label
  }));

  readonly frozenColumnsInOrder = computed(() => {
    return this.frozenColumnsService.getFrozenColumnsInOrder(
      this.ALL_COLUMN_KEYS
    );
  });

  readonly visibleColumnsComputed = computed(() => {
    const frozenInOrder = this.frozenColumnsService.getFrozenColumnsInOrder(
      this.ALL_COLUMN_KEYS
    );

    const nonFrozenInOrder =
      this.frozenColumnsService.getNonFrozenColumnsInOrder(
        this.ALL_COLUMN_KEYS
      );

    const orderedKeys = [...frozenInOrder, ...nonFrozenInOrder];

    return orderedKeys
      .map((key) => this.ALL_COLUMNS.find((col) => col.key === key))
      .filter((col): col is (typeof this.ALL_COLUMNS)[0] => col !== undefined);
  });

  VISIBLE_COLUMNS = this.ALL_COLUMNS;

  override tableActions: TableActionType[] = [];

  override searchFieldOptions = RECEIVED_ORDER_SEARCH_OPTIONS;

  constructor(
    private readonly presenter: ReceivedOrdersPresenter,
    public readonly frozenColumnsService: FrozenColumnsService,
    private readonly cdr: ChangeDetectorRef,
    private readonly toastService: ToastService
  ) {
    super();
    this.setupTableActions();
  }

  protected getOrderStatus(): OrderStatus {
    return OrderStatus.RECIBIDA;
  }

  protected getMetricsService(): IOrderMetricsPort {
    return {
      getMetrics: () => this.presenter.getMetrics(),
      getMetricsStream: (status: OrderStatus, interval: number) =>
        this.presenter.getMetricsStream(interval)
    };
  }

  protected getCdr(): ChangeDetectorRef {
    return this.cdr;
  }

  protected getToastService(): ToastService {
    return this.toastService;
  }

  getFilterOptionsService(): IFilterOptionsQueryPort {
    return {
      getFilterOptions: () => this.presenter.getFilterOptions()
    };
  }

  protected override loadMetrics(): void {
    this.metricsSubscription = this.getMetricsService()
      .getMetricsStream(this.getOrderStatus(), 60000)
      .subscribe((metrics) => {
        this.dashboardMetrics = [
          {
            label: 'Hoy',
            value: 'Hoy',
            isDate: true,
            icon: 'icon-calendario-fecha'
          },
          { label: 'Total muestras', value: metrics.totalOrders },
          { label: 'Muestras ingresadas', value: metrics.ordersToday }
        ];

        if (metrics.reprocesses !== undefined && metrics.reprocesses !== null) {
          this.dashboardMetrics.push({
            label: 'Reprocesos',
            value: metrics.reprocesses
          });
        }
      });
  }

  protected loadOrdersInternal(): Observable<void> {
    let queryParams: IngestedOrdersQueryParams = {
      ...this.buildBaseQueryParams(),
      sortBy: 'admissionDate',
      sortDirection: 'ASC'
    };

    queryParams = this.addFilterParamsToQuery(queryParams);

    return this.presenter.getOrders(queryParams).pipe(
      tap((response) => {
        this.orders = this.sortOrdersByLgNumberGroup(response.orders);
        this.totalRecords = response.pagination.total;
        this.cdr.detectChanges();
      }),
      map(() => void 0),
      catchError((error) => {
        console.error('Error loading orders:', error);
        return EMPTY;
      })
    );
  }

  protected resetToFirstPageAndReload(): void {
    if (this.paginator) {
      this.paginator.specificPage(0);
    } else {
      this.currentPage = 0;
      this.loadOrdersInternal().subscribe();
    }
  }

  onPageChange(pagination: {
    page: number;
    pageSize: number;
    total: number;
  }): void {
    this.currentPage = pagination.page;
    this.pageSize = pagination.pageSize;
    this.loadOrdersInternal().subscribe();
  }

  private setupTableActions(): void {
    this.tableActions = [
      {
        icon: 'icon-editar',
        function: (orderIdOrObject: ReceivedOrder | string | number) => {
          if (
            typeof orderIdOrObject === 'string' ||
            typeof orderIdOrObject === 'number'
          ) {
            const order = this.orders.find((o) => o.id === orderIdOrObject);
            if (order) {
              this.onEditOrder(order);
            }
          } else {
            this.onEditOrder(orderIdOrObject);
          }
        },
        functionProperty: 'id'
      },
      {
        icon: 'icon-basurero',
        function: (orderIdOrObject: ReceivedOrder | string | number) => {
          if (
            typeof orderIdOrObject === 'string' ||
            typeof orderIdOrObject === 'number'
          ) {
            const order = this.orders.find((o) => o.id === orderIdOrObject);
            if (order) {
              this.onDeleteOrder(order);
            }
          } else {
            this.onDeleteOrder(orderIdOrObject);
          }
        },
        functionProperty: 'id'
      }
    ];
  }

  onDeleteOrder(order: ReceivedOrder): void {
    const specimenCode = order.caseNumber;
    if (!specimenCode) {
      this.toastService.setToast({
        title: 'No se puede eliminar',
        message: 'La orden no tiene código de muestra asignado',
        type: ToastTypeEnum.ERROR
      });
      return;
    }
    this.deleteSpecimenCode.set(specimenCode);
    this.showDeleteConfirmModal.set(true);
  }

  onConfirmDelete(): void {
    const specimenCode = this.deleteSpecimenCode();
    if (!specimenCode) {
      return;
    }

    this.isDeleting.set(true);
    this.presenter.deleteSample(specimenCode).subscribe({
      next: () => {
        this.isDeleting.set(false);
        this.closeDeleteModal();
        this.toastService.setToast({
          title: 'Muestra eliminada',
          message: 'La muestra ha sido eliminada correctamente',
          type: ToastTypeEnum.SUCCESS
        });
        this.loadOrdersInternal().subscribe();
        this.refreshMetrics();
      },
      error: () => {
        this.isDeleting.set(false);
        this.toastService.setToast({
          title: 'Error al eliminar',
          message: 'No se pudo eliminar la muestra. Intente nuevamente.',
          type: ToastTypeEnum.ERROR
        });
      }
    });
  }

  private refreshMetrics(): void {
    this.presenter.getMetrics().subscribe((metrics) => {
      this.dashboardMetrics = [
        { label: 'Hoy', value: 'Hoy', isDate: true },
        { label: 'Total muestras', value: metrics.totalOrders },
        { label: 'Muestras ingresadas', value: metrics.ordersToday }
      ];

      if (metrics.reprocesses !== undefined && metrics.reprocesses !== null) {
        this.dashboardMetrics.push({
          label: 'Reprocesos',
          value: metrics.reprocesses
        });
      }
    });
  }

  closeDeleteModal(): void {
    this.showDeleteConfirmModal.set(false);
    this.deleteSpecimenCode.set(null);
  }

  onEditOrder(order: ReceivedOrder): void {
    if (!order.caseNumber) {
      this.toastService.setToast({
        title: 'No se puede editar',
        message: 'La orden no tiene número de caso asignado',
        type: ToastTypeEnum.ERROR
      });
      return;
    }

    this.editCaseNumber.set(order.caseNumber);
    this.editSampleData.set(null);
    this.showEditSampleModal.set(true);

    this.presenter.getSampleByCaseNumber(order.caseNumber).subscribe({
      next: (data) => {
        this.editSampleData.set(data);
      },
      error: (error) => {
        console.error('Error loading sample data:', error);
        this.showEditSampleModal.set(false);
        this.toastService.setToast({
          title: 'Error al cargar datos',
          message: 'No se pudieron cargar los datos de la muestra',
          type: ToastTypeEnum.ERROR
        });
      }
    });
  }

  closeEditSampleModal(): void {
    this.showEditSampleModal.set(false);
    this.editCaseNumber.set(null);
    this.editSampleData.set(null);
    this.loadOrdersInternal().subscribe();
  }

  handleEditSuccess(): void {
    this.closeEditSampleModal();
  }

  handleSelectionChange(selectedItems: ReceivedOrder[]): void {
    this.selectedOrders = selectedItems;

    const currentIds = this._selectedOrderIds();
    const newIds = new Set(currentIds);

    const currentPageIds = this.orders.map((order) => order.id);

    for (const id of currentPageIds) {
      const isStillSelected = selectedItems.some((item) => item.id === id);
      if (!isStillSelected) {
        newIds.delete(id);
      }
    }

    for (const item of selectedItems) {
      newIds.add(item.id);
    }

    this._selectedOrderIds.set(newIds);
  }

  clearSelections(): void {
    this._selectedOrderIds.set(new Set());
    this.selectedOrders = [];
  }

  handleFinalSave(): void {
    const selectedCount = this._selectedOrderIds().size;

    if (selectedCount === 0) {
      this.toastService.setToast({
        title: 'No hay muestras seleccionadas',
        type: ToastTypeEnum.WARNING
      });
      return;
    }

    this._finalSaveModalState.set(FinalSaveModalState.CONFIRMATION);
    this._finalSaveResponse.set(null);
    this._finalSaveModalOpen.set(true);
  }

  onFinalSaveModalConfirm(): void {
    this.executeFinalSave();
  }

  onFinalSaveModalClose(): void {
    if (this._finalSaveModalState() !== FinalSaveModalState.PROCESSING) {
      this._finalSaveModalOpen.set(false);
      if (this._finalSaveModalState() === FinalSaveModalState.SUCCESS) {
        this.refreshAfterSuccess();
      }
    }
  }

  private executeFinalSave(): void {
    const caseNumbers = this.selectedOrders.map((order) => order.caseNumber);

    this._finalSaveModalState.set(FinalSaveModalState.PROCESSING);
    this._isProcessing.set(true);

    this.presenter.finalSave(caseNumbers).subscribe({
      next: (response) => {
        this._finalSaveResponse.set(response);
        this._finalSaveModalState.set(FinalSaveModalState.SUCCESS);
        this._isProcessing.set(false);
      },
      error: (error) => {
        console.error('Error en guardado definitivo:', error);

        this._finalSaveModalOpen.set(false);
        this._isProcessing.set(false);

        const errorMessage = this.getErrorMessage(error);
        this.toastService.setToast({
          title: 'Error en Guardado Definitivo',
          type: ToastTypeEnum.ERROR,
          message: errorMessage
        });
      }
    });
  }

  private getErrorMessage(error: any): string {
    if (error.status === 400) {
      return 'Solicitud inválida. Verifique las muestras seleccionadas.';
    } else if (error.status === 409) {
      return 'Algunas muestras ya fueron procesadas. Actualice la pantalla.';
    } else if (error.status === 422) {
      return 'Algunas muestras ya no están en estado válido. Actualice y reintente.';
    } else if (error.status === 503) {
      return 'Servicio no disponible temporalmente. Intente más tarde.';
    } else if (error.name === 'TimeoutError') {
      return 'La operación está tardando más de lo esperado. Verifique el estado de las muestras.';
    } else {
      return 'Error interno del sistema. Contacte al administrador.';
    }
  }

  private refreshAfterSuccess(): void {
    this.clearSelections();

    this.loadOrdersInternal().subscribe();

    this.loadMetrics();
  }

  /**
   * Ordena las órdenes agrupando por familia (número base del LG).
   * Dentro de cada grupo, el afectado (sin sufijo) aparece primero,
   * seguido de los familiares (con sufijos) ordenados alfabéticamente.
   */
  private sortOrdersByLgNumberGroup(orders: ReceivedOrder[]): ReceivedOrder[] {
    return [...orders].sort((a, b) => {
      const lgA = a.lgNumber || '';
      const lgB = b.lgNumber || '';

      // Extraer número base y sufijo del LG
      const parseLG = (lg: string) => {
        // Patrón: LG26XXXXXXX o LG26XXXXXXXSUFIJO
        const match = lg.match(/^(LG\d+?)([A-Z]+)?$/i);
        if (match) {
          return {
            base: match[1],           // Ej: "LG2600001"
            suffix: match[2] || '',   // Ej: "FP", "M", "P" o ""
            numericPart: parseInt(match[1].replace(/\D/g, ''), 10) || 0  // Ej: 2600001
          };
        }
        return { base: lg, suffix: '', numericPart: 0 };
      };

      const parsedA = parseLG(lgA);
      const parsedB = parseLG(lgB);

      // 1. Ordenar primero por número base
      if (parsedA.numericPart !== parsedB.numericPart) {
        return parsedA.numericPart - parsedB.numericPart;
      }

      // 2. Dentro del mismo LG base, el afectado (sin sufijo) va primero
      if (parsedA.suffix === '' && parsedB.suffix !== '') {
        return -1;
      }
      if (parsedA.suffix !== '' && parsedB.suffix === '') {
        return 1;
      }

      // 3. Si ambos tienen sufijo, ordenar alfabéticamente por sufijo
      return parsedA.suffix.localeCompare(parsedB.suffix);
    });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.toastService.setToast(null);
  }
}
